import moment from "moment";

export const KOT_STORAGE_DATE_FORMAT = "YYYY-MM-DD";
export const KOT_DISPLAY_DATE_FORMAT = "MM-DD-YYYY";
export const KOT_DISPLAY_DATE_TIME_FORMAT = "MM-DD-YYYY hh:mm A";
export const KOT_DATE_HELPER_TEXT = `Enter a valid date in ${KOT_DISPLAY_DATE_FORMAT} format.`;
export const KOT_DATE_ERROR_MESSAGE = `Please enter a valid date in ${KOT_DISPLAY_DATE_FORMAT} format.`;

export const parseKotStoredDate = (value) => {
  if (!value) return null;

  const parsedValue = moment(value, KOT_STORAGE_DATE_FORMAT, true);
  return parsedValue.isValid() ? parsedValue : null;
};

const getKotDisplayMoment = (value) => {
  if (!value) return null;

  const strictParsedValue = moment(value, [KOT_STORAGE_DATE_FORMAT, moment.ISO_8601], true);
  if (strictParsedValue.isValid()) {
    return strictParsedValue;
  }

  const fallbackParsedValue = moment(value);
  return fallbackParsedValue.isValid() ? fallbackParsedValue : null;
};

export const formatKotDate = (value, fallback = "-") => {
  const parsedValue = getKotDisplayMoment(value);
  return parsedValue ? parsedValue.format(KOT_DISPLAY_DATE_FORMAT) : fallback;
};

export const formatKotDateTime = (value, fallback = "-") => {
  const parsedValue = getKotDisplayMoment(value);
  return parsedValue ? parsedValue.format(KOT_DISPLAY_DATE_TIME_FORMAT) : fallback;
};

export const toKotStorageDate = (value) => {
  if (!value || typeof value.isValid !== "function" || !value.isValid()) {
    return "";
  }

  return value.format(KOT_STORAGE_DATE_FORMAT);
};

/**
 * Decide what a KOT DatePicker's `onChange` should do with the value it emits.
 *
 * MUI x-date-pickers v5 validates the *controlled* `value` prop (see
 * `internals/hooks/validation/useValidation`), not the text sitting in the input. A date
 * typed by hand that moment cannot parse is therefore never handed to the picker's own
 * validator, so `onError` never fires for it and the field silently keeps whatever was
 * stored before. Callers use this to flag the bad entry themselves.
 *
 * `shouldStore: false` means "leave the stored value alone" — writing "" back on every
 * unparseable keystroke would blank the input while the user is still part-way through
 * typing a date.
 *
 * @param {import("moment").Moment | null} pickerValue value emitted by DatePicker onChange
 * @returns {{ isInvalid: boolean, shouldStore: boolean, storedDate: string }}
 */
export const resolveKotDateChange = (pickerValue) => {
  if (!pickerValue) {
    return { isInvalid: false, shouldStore: true, storedDate: "" };
  }

  if (typeof pickerValue.isValid !== "function" || !pickerValue.isValid()) {
    return { isInvalid: true, shouldStore: false, storedDate: "" };
  }

  return { isInvalid: false, shouldStore: true, storedDate: toKotStorageDate(pickerValue) };
};
